import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";
        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "24";
const height = "24";
const viewBox = "0 0 24 24";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.midnight}
            fillRule="evenodd"
            d="M21.999 11.955l.001.008.001-.003v.074c0 .005 0 .009-.002.013a.06.06 0 00-.001.012v.013a.047.047 0 00-.003.017.082.082 0 00-.003.021l-.001.003a.75.75 0 01-.21.412c-.208.249-.438.482-.669.711-1.246 1.237-2.677 2.339-4.242 3.14-1 .512-2.07.923-3.179 1.121a9.41 9.41 0 01-3.652-.049c-2.097-.446-4-1.554-5.662-2.878-.772-.616-1.531-1.288-2.169-2.044-.279-.331-.279-.723 0-1.054.209-.249.44-.482.669-.711C4.124 9.525 5.554 8.42 7.12 7.621c1-.511 2.069-.924 3.178-1.122a9.435 9.435 0 013.653.051c2.099.447 4 1.554 5.661 2.879.772.615 1.531 1.288 2.169 2.044a.75.75 0 01.21.411v.003l.002.01.002.011.001.018a.045.045 0 01.001.012l.002.017zM8.152 12a3.847 3.847 0 107.695 0 3.848 3.848 0 10-7.695 0zm5.121 0a1.273 1.273 0 10-2.547 0 1.273 1.273 0 002.547 0z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Eye"
});
